import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { StoryblokDirective } from '../../../directives/storyblok.directive';
import {
  RdsAvatarModule,
  RdsButtonModule,
  RdsDividerModule,
  RdsHeaderModule,
  RdsIconsModule,
  RdsListItemModule,
  RdsMenuModule,
  RdsMenuTriggerDirective,
  RdsSearchModule,
} from '@rds/angular-components';
import { Router, RouterLink } from '@angular/router';
import { OverlayModule } from '@angular/cdk/overlay';

import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NavigationDirective } from '../../../directives/navigation.directive';
import { FeatureFlagDirective } from '../../../directives/feature-flag.directive';
import { MegaMenuComponent } from '../mega-menu/mega-menu.component';
import { MenuSection } from '../../../models/mega-menu';
import { OfferingsService } from '../../../services/offerings.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'inexp-header',
  imports: [
    StoryblokDirective,
    NavigationDirective,
    FeatureFlagDirective,
    RouterLink,
    RdsAvatarModule,
    RdsButtonModule,
    RdsDividerModule,
    RdsHeaderModule,
    RdsIconsModule,
    RdsSearchModule,
    OverlayModule,
    RdsMenuModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    RdsListItemModule,
    MegaMenuComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  host: {
    '(window:scroll)': 'onWindowScroll()',
    '(window:resize)': 'updateHeaderHeight()',
  },
})
export class HeaderComponent implements AfterViewInit, OnDestroy {
  private readonly _authService = inject(AuthService);
  private readonly _zone = inject(NgZone);
  private _resizeObserver?: ResizeObserver;
  offeringsService = inject(OfferingsService);

  router = inject(Router);
  _editable = input<string>();
  _uid = input<string>();
  component = input<string>();

  appTitle = input<string>();
  navigation = input<any[]>([]);
  secondaryNavigation = input<any[]>([]);
  showFeedbackButton = input<boolean>(false);
  showNotifications = input<boolean>(false);
  feedbackButtonTitle = input<string>('');
  showSearch = input<boolean>(false);
  showSecondaryNavigation = input<boolean>(false);

  isHeaderHidden = signal(false);
  lastScrollTop = 0;
  scrollThreshold = 100;
  hideThreshold = 100;

  readonly triggerProfileMenu = viewChild(RdsMenuTriggerDirective);
  readonly headerRef = viewChild.required<ElementRef>('headerRef');
  offsetHeight = signal(0);

  initials = this._authService.initials;

  activeMenu = signal<MenuSection | null>(null);
  activeMenuData = computed(() => {
    const menuName = this.activeMenu()?.menuName;

    if (menuName === 'offerings') {
      return this.offeringsService.offeringsByCategories();
    }

    return [];
  });

  /** Offerings menu open while its data is still loading → show skeleton. */
  activeMenuLoading = computed(
    () =>
      this.activeMenu()?.menuName === 'offerings' &&
      this.offeringsService.isLoadingOfferings(),
  );

  ngAfterViewInit() {
    this.updateHeaderHeight();
    this._observeHeaderHeight();
  }

  ngOnDestroy() {
    this._resizeObserver?.disconnect();
  }

  /**
   * The header is `position: fixed`, so `.header-spacer` holds its place in the
   * flow and has to match its height. That height is not constant: the
   * secondary navigation bar and the search bar each render conditionally.
   *
   * Measuring on init alone used to be enough because Focus and Panorama were
   * separate routed layouts, each with its own header instance, so every mode
   * switch re-ran `ngAfterViewInit`. The shell now keeps a single instance and
   * only swaps its inputs, so the element resizes without any lifecycle hook
   * firing — leaving a gap below the Focus header, or the secondary bar sitting
   * over page content in Panorama, depending on which mode loaded first.
   *
   * Observing the element covers every cause of a height change, including the
   * responsive reflow the resize listener handled and late font loading.
   */
  private _observeHeaderHeight() {
    const element = this.headerRef()?.nativeElement;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    // Not all browsers deliver these callbacks inside the Angular zone.
    this._resizeObserver = new ResizeObserver(() =>
      this._zone.run(() => this.updateHeaderHeight()),
    );
    this._resizeObserver.observe(element);
  }

  onLogoTriggered() {
    this.router.navigate(['/']);
  }

  updateHeaderHeight() {
    const headerRef = this.headerRef();
    if (headerRef?.nativeElement) {
      this.offsetHeight.set(headerRef.nativeElement.offsetHeight);
    }
  }

  onWindowScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    if (scrollTop <= this.hideThreshold) {
      this.isHeaderHidden.set(false);
      this.lastScrollTop = scrollTop;
      this.activeMenu.set(null);
      return;
    }

    if (Math.abs(scrollTop - this.lastScrollTop) < this.scrollThreshold) {
      return;
    }

    if (scrollTop > this.lastScrollTop) {
      this.isHeaderHidden.set(true);
    }

    this.lastScrollTop = scrollTop;
    this.activeMenu.set(null);
  }

  toggleMegaMenu(navSection: MenuSection) {
    this.activeMenu() === navSection
      ? this.activeMenu.set(null)
      : this.activeMenu.set(navSection);
  }

  closeMegaMenu() {
    this.activeMenu.set(null);
  }

  handleProfileMenu() {
    this.triggerProfileMenu()?.openMenu();
  }

  logout() {
    this._authService.logout();
  }
}
